/**
 * StarterModal.tsx
 *
 * Reusable first-use orientation modal for each screen.
 * Shows once automatically, reopenable via (i) icon.
 *
 * Layout: title → description → 3–4 bullets → why line → Got it button
 * Style: clean, centered, neutral, supportive — no marketing tone.
 *
 * Target: src/components/ui/StarterModal.tsx
 */


import { useEffect, useRef } from 'react';

export interface StarterModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  title: string;
  description: string;
  bullets: string[];
  why: string;
}

export function StarterModal({
  isOpen,
  onDismiss,
  title,
  description,
  bullets,
  why,
}: StarterModalProps) {
  const btnRef = useRef<HTMLButtonElement>(null);

  // Focus the Got it button on open (accessibility)
  useEffect(() => {
    if (isOpen) setTimeout(() => btnRef.current?.focus(), 50);
  }, [isOpen]);

  // Dismiss on Escape key
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') onDismiss(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onDismiss}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9000,
          background: 'rgba(0,0,0,0.40)',
        }}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="starter-modal-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 24px',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 20,
            padding: '24px 20px 20px',
            maxWidth: 380,
            width: '100%',
            pointerEvents: 'auto',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Title */}
          <p
            id="starter-modal-title"
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: '#1a2a2a',
              letterSpacing: -0.4,
              margin: '0 0 6px',
              lineHeight: 1.3,
            }}
          >
            {title}
          </p>

          {/* Description */}
          <p
            style={{
              fontSize: 13,
              color: '#6B7280',
              lineHeight: 1.6,
              margin: '0 0 14px',
            }}
          >
            {description}
          </p>

          {/* Bullets */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
            {bullets.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: '#007c86',
                    flexShrink: 0,
                    marginTop: 6,
                  }}
                />
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{b}</span>
              </div>
            ))}
          </div>

          {/* Why line */}
          <p
            style={{
              fontSize: 12,
              color: '#9CA3AF',
              fontStyle: 'italic',
              lineHeight: 1.5,
              margin: '0 0 18px',
            }}
          >
            {why}
          </p>

          {/* Got it button */}
          <button
            type="button"
            ref={btnRef}
            onClick={onDismiss}
            style={{
              width: '100%',
              background: '#007c86',
              color: '#fff',
              borderRadius: 20,
              padding: '11px 0',
              fontSize: 14,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </>
  );
}

// ── Info icon button — place in screen headers ────────────────────────────────

export function StarterModalInfoButton({ onPress }: { onPress: () => void }) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label="Screen information"
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: '1.5px solid rgba(0,124,134,0.3)',
        background: 'rgba(0,124,134,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007c86" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    </button>
  );
}
